'use client';

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SDivider } from '../SDivider';

/** 0(또는 미집계)은 죽은 값이라 어느 쪽이든 count-null. */
const COUNT_NULL = 'var(--cmp-table-bar-count-null)';

/** 전체 건수 색 — 1건 이상이면 count-total. */
const totalColor = (n: number) => (n > 0 ? 'var(--cmp-table-bar-count-total)' : COUNT_NULL);

/**
 * 선택 건수 색 — 선택이 있으면 brilliantblue_75 로 강조한다.
 * 전체 건수(count-total)보다 밝은 파랑이라 "지금 선택된 상태"가 눈에 먼저 들어온다.
 */
const selectedColor = (n: number) => (n > 0 ? 'var(--color-brilliantblue-75)' : COUNT_NULL);

export interface STableBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 전체 건수. 지정하면 "총 N 건" 을 표시한다 (천단위 구분 자동) */
  total?: number;
  /** 선택 건수. 지정하면 "N건 선택" 을 표시한다 */
  selected?: number;
  /** 구분선 오른쪽 좌측 액션 영역 */
  actions?: ReactNode;
  /** 우측 정렬 액션 영역 */
  rightActions?: ReactNode;
}

/**
 * STableBar — STable 위에 얹는 툴바.
 * 왼쪽에 건수 요약(총 N 건 / N건 선택), 구분선 뒤에 좌측 액션, 오른쪽 끝에 우측 액션을 둔다.
 * 색·간격은 모두 테이블 컴포넌트 토큰(--cmp-table-bar-*) 기반.
 */
export const STableBar = forwardRef<HTMLDivElement, STableBarProps>(function STableBar(
  { total, selected, actions, rightActions, className, ...rest },
  ref,
) {
  const hasTotal = total != null;
  const hasSelected = selected != null;
  const hasCount = hasTotal || hasSelected;

  return (
    <div
      ref={ref}
      // 액션이 하나도 없으면 조작 대상이 없는 요약 텍스트 줄이라 toolbar 롤을 붙이지 않는다.
      role={actions || rightActions ? 'toolbar' : undefined}
      className={cn(
        'flex w-full items-center gap-[var(--cmp-table-bar-section-gap)] px-[var(--sys-space-table-padding-md)]',
        'h-[var(--cmp-table-bar-height)] shrink-0',
        'rounded-[var(--cmp-table-radius)] border-[length:var(--cmp-table-border-width)] border-solid border-[var(--cmp-table-border-color)] bg-white',
        className,
      )}
      {...rest}
    >
      {/* 건수 요약은 한 섹션이라 내부 간격만 content-gap(8px), 바깥 구분선까지는 section-gap(12px). */}
      {hasCount && (
        <div className="flex items-center gap-[var(--cmp-table-content-gap)]">
          {hasTotal && (
            <span className="typo-table-accent whitespace-nowrap text-[var(--sys-color-fg-primary)]">
              총 <b style={{ color: totalColor(total) }}>{total.toLocaleString()}</b> 건
            </span>
          )}

          {hasTotal && hasSelected && (
            <span
              aria-hidden="true"
              className="typo-table-body text-[var(--sys-color-fg-tertiary)]"
            >
              /
            </span>
          )}

          {hasSelected && (
            <span className="typo-table-body whitespace-nowrap text-[var(--sys-color-fg-primary)]">
              <b style={{ color: selectedColor(selected) }}>{selected.toLocaleString()}</b> 건 선택
            </span>
          )}
        </div>
      )}

      {/*
        actions 가 Fragment 라 truthy 인데 내부 분기로 아무것도 렌더되지 않는 경우가 있다.
        React 로는 알 수 없으므로 슬롯이 실제로 비었는지(:empty)를 CSS 로 보고
        구분선까지 통째로 감춘다. 구분선을 이 래퍼 안에 둔 이유도 그것.
      */}
      {actions && (
        <div className="flex items-center gap-[var(--cmp-table-bar-section-gap)] has-[>div:empty]:hidden">
          {hasCount && (
            <SDivider
              vertical
              className="h-[var(--cmp-table-bar-divider-height)] self-center"
              // 색은 SDivider 가 인라인 style 로 깔므로 클래스가 아니라 style 로 덮어써야 한다.
              style={{ backgroundColor: 'var(--cmp-table-border-color)' }}
            />
          )}
          <div className="flex items-center gap-[var(--cmp-table-content-gap)]">{actions}</div>
        </div>
      )}

      {rightActions && (
        <div className="ml-auto flex items-center gap-[var(--cmp-table-content-gap)] empty:hidden">
          {rightActions}
        </div>
      )}
    </div>
  );
});
